// ─── AccountingCodesView — códigos contabilísticos (BM Petrotec) ─────────
//
// Catálogo dos 18 códigos do BM oficial Petrotec. Agrupados por categoria
// (labor / travel / material / service) com preço H.N. e opcionalmente H.E.
// para mão de obra. Bridge futura para o Primavera ERP.

import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip, Switch,
  FormControlLabel, CircularProgress, Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { useDispatch } from 'react-redux';
import { setActiveView } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';

const CATEGORIES = [
  { key: 'labor',    labelPt: 'Mão de obra',    labelEn: 'Labor',       color: '#dc2626', bg: '#fee2e2' },
  { key: 'travel',   labelPt: 'Deslocações',    labelEn: 'Travel',      color: '#0ea5e9', bg: '#e0f2fe' },
  { key: 'material', labelPt: 'Materiais',      labelEn: 'Materials',   color: '#f59e0b', bg: '#fef3c7' },
  { key: 'service',  labelPt: 'Serviços',       labelEn: 'Services',    color: '#22c55e', bg: '#dcfce7' },
];
const CAT_META = CATEGORIES.reduce((acc, c) => { acc[c.key] = c; return acc; }, {});

const UNITS = [
  { value: 'h',         label: 'Hora (h)' },
  { value: 'km',        label: 'Quilómetro (km)' },
  { value: 'un',        label: 'Unidade (un)' },
  { value: 'dia',       label: 'Dia' },
  { value: 'refeição',  label: 'Refeição' },
  { value: 'mês',       label: 'Mês' },
];

const EMPTY_FORM = {
  code: '', description: '', category: 'labor',
  unit: 'h', priceHN: '', priceHE: '', hasOvertime: true, notes: '',
};

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff', borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  title: { fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
  subtitle: { fontSize: '0.7rem', color: '#64748b', marginTop: 2 },
  toolbar: {
    display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1, maxWidth: 360,
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#fff', fontSize: '0.78rem' },
  },
  addButton: {
    background: '#3b82f6', color: '#fff', borderRadius: 8,
    textTransform: 'none', fontWeight: 700, padding: '6px 16px',
    fontSize: '0.74rem', boxShadow: 'none',
    '&:hover': { background: '#2563eb', boxShadow: '0 2px 6px rgba(59,130,246,0.25)' },
  },
  saveButton: {
    background: '#3b82f6', color: '#fff',
    textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
    padding: '6px 18px', boxShadow: 'none',
    '&:hover': { background: '#2563eb' },
  },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  categorySection: { marginBottom: 16 },
  categoryHeader: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
    borderRadius: 8, marginBottom: 8,
    fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  card: {
    background: '#fff', borderRadius: 10, padding: '12px 16px', marginBottom: 6,
    border: '1px solid #e2e8f0', display: 'grid',
    gridTemplateColumns: '120px 1fr auto auto auto',
    gap: 12, alignItems: 'center',
    transition: 'all 0.15s',
    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)', borderColor: '#cbd5e1' },
  },
  codeBadge: {
    fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 800,
    color: '#475569', background: '#f1f5f9', padding: '4px 8px', borderRadius: 6,
    width: 'fit-content',
  },
  priceBox: { textAlign: 'right' },
  priceVal: { fontSize: '0.84rem', fontWeight: 800, color: '#0f172a' },
  priceLabel: { fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase' },
  cardActions: { display: 'flex', gap: 4 },
  empty: {
    padding: '80px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  formCard: {
    background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
    border: '1px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  formTitle: { fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
  formRow2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  formRow3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 },
  formRow4: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
}));

function AccountingCodesView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  // Moeda do tenant — preços H.N./H.E. estão guardados nesta unidade
  // (sem coluna `currency` na tabela accounting_codes; vivem implicitos
  // na defaultCurrency do ClientSettings). Mostrar no UI é essencial
  // para o operador não pensar que está a trabalhar em €.
  const currencyCode = clientSettings?.defaultCurrency || 'EUR';
  const currencySymbol = { EUR: '€', AOA: 'Kz', USD: '$', NAD: 'N$', ZAR: 'R' }[currencyCode] || currencyCode;
  const pt = lang === 'pt';

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Ref ao card de edição. Quando o operador clica "Editar" num código
  // que está abaixo da viewport (a lista pode ter 19+ códigos), o
  // formulário renderiza no topo da página e não fica visível. Este ref
  // serve para fazer scrollIntoView automático.
  const formCardRef = useRef(null);
  useEffect(() => {
    if (editingId !== null && formCardRef.current) {
      requestAnimationFrame(() => {
        formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [editingId]);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const qs = showInactive ? '?includeInactive=1' : '';
      const r = await fetch(`/planner-api/accounting-codes${qs}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setCodes(await r.json());
    } catch (e) {
      setError(pt ? 'Não foi possível carregar.' : 'Could not load.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function startCreate() { setForm(EMPTY_FORM); setEditingId('new'); }
  function startEdit(c) {
    setForm({
      code: c.code,
      description: c.description,
      category: c.category,
      unit: c.unit || '',
      priceHN: c.priceHN != null ? String(c.priceHN) : '',
      priceHE: c.priceHE != null ? String(c.priceHE) : '',
      hasOvertime: !!c.hasOvertime,
      notes: c.notes || '',
    });
    setEditingId(c.id);
  }
  function cancelEdit() { setEditingId(null); setForm(EMPTY_FORM); }

  async function saveForm() {
    if (!form.code.trim()) { setError(pt ? 'Código obrigatório.' : 'Code required.'); return; }
    if (!form.description.trim()) { setError(pt ? 'Descrição obrigatória.' : 'Description required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const isNew = editingId === 'new';
      const body = JSON.stringify({
        ...(isNew ? { code: form.code.trim() } : {}),
        description: form.description.trim(),
        category: form.category,
        unit: form.unit || null,
        priceHN: form.priceHN === '' ? null : Number(form.priceHN),
        priceHE: form.priceHE === '' ? null : Number(form.priceHE),
        hasOvertime: !!form.hasOvertime,
        notes: form.notes.trim() || null,
      });
      const url = isNew ? '/planner-api/accounting-codes' : `/planner-api/accounting-codes/${editingId}`;
      const r = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers, body });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchAll();
    } catch (e) {
      setError(e.message || (pt ? 'Erro a guardar.' : 'Save error.'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c) {
    try {
      const r = await fetch(`/planner-api/accounting-codes/${c.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ isActive: !c.isActive }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) { setError(e.message); }
  }

  async function confirmDeleteNow() {
    if (!deleteConfirm) return;
    try {
      const r = await fetch(`/planner-api/accounting-codes/${deleteConfirm.id}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDeleteConfirm(null);
      await fetchAll();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  }

  const filtered = codes.filter(c => {
    if (filterCategory && c.category !== filterCategory) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [c.code, c.description, c.notes].some(v => v && String(v).toLowerCase().includes(q));
  });

  // Agrupar por categoria para visualização em secções
  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: filtered.filter(c => c.category === cat.key),
  })).filter(g => g.items.length > 0);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back to Maintenance'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Typography className={classes.title}>
            <ReceiptIcon style={{ fontSize: 20, color: '#3b82f6' }} />
            {pt ? 'Códigos Contabilísticos' : 'Accounting Codes'}
            <span style={{ marginLeft: 10, padding: '2px 8px', background: '#f0f9ff', color: '#0369a1', border: '1px solid #e0f2fe', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700 }}>
              {currencySymbol}
            </span>
          </Typography>
          <Typography className={classes.subtitle}>
            {pt
              ? 'Códigos do BM oficial Petrotec com preço H.N. e H.E. — ponte para o Primavera'
              : 'Petrotec BM accounting codes with H.N./H.E. prices — Primavera bridge'}
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField className={classes.searchInput}
          size="small" variant="outlined"
          placeholder={pt ? 'Procurar por código ou descrição…' : 'Search by code or description…'}
          value={search} onChange={e => setSearch(e.target.value)} />
        <FormControl size="small" variant="outlined" style={{ minWidth: 160 }}>
          <InputLabel>{pt ? 'Categoria' : 'Category'}</InputLabel>
          <Select label={pt ? 'Categoria' : 'Category'}
            value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <MenuItem value="">{pt ? 'Todas' : 'All'}</MenuItem>
            {CATEGORIES.map(c => <MenuItem key={c.key} value={c.key}>{pt ? c.labelPt : c.labelEn}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch size="small" checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)} />}
          label={<Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
            {pt ? 'Mostrar desactivados' : 'Show inactive'}
          </Typography>} />
        <Box style={{ flex: 1 }} />
        <Button className={classes.addButton} startIcon={<AddIcon style={{ fontSize: 18 }} />}
          onClick={startCreate} disabled={editingId !== null}>
          {pt ? 'Novo código' : 'New code'}
        </Button>
      </Box>

      <Box className={classes.list}>
        {error && (
          <Box style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', color: '#991b1b',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>{error}</Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <ReceiptIcon style={{ fontSize: 18, color: '#3b82f6' }} />
              {editingId === 'new' ? (pt ? 'Novo código' : 'New code') : (pt ? 'Editar código' : 'Edit code')}
            </Typography>
            <Box className={classes.formRow2}>
              <TextField label={pt ? 'Código *' : 'Code *'}
                size="small" variant="outlined" value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
                disabled={editingId !== 'new'} placeholder="700100A09" autoFocus />
              <FormControl size="small" variant="outlined">
                <InputLabel>{pt ? 'Categoria *' : 'Category *'}</InputLabel>
                <Select label={pt ? 'Categoria *' : 'Category *'}
                  value={form.category}
                  onChange={e => {
                    const cat = e.target.value;
                    setForm({ ...form, category: cat,
                      hasOvertime: cat === 'labor',
                      unit: cat === 'labor' ? 'h' : cat === 'travel' ? 'km' : 'un',
                    });
                  }}>
                  {CATEGORIES.map(c => <MenuItem key={c.key} value={c.key}>{pt ? c.labelPt : c.labelEn}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <TextField label={pt ? 'Descrição *' : 'Description *'}
              size="small" variant="outlined" fullWidth
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder={pt ? 'Mão de obra Tec. Inst. Mecânicas' : 'Labor description'} />
            <Box className={classes.formRow4}>
              <FormControl size="small" variant="outlined">
                <InputLabel>{pt ? 'Unidade' : 'Unit'}</InputLabel>
                <Select label={pt ? 'Unidade' : 'Unit'} value={form.unit}
                  onChange={e => setForm({ ...form, unit: e.target.value })}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label={pt ? 'Preço H.N.' : 'Price H.N.'}
                size="small" variant="outlined" type="number"
                value={form.priceHN}
                onChange={e => setForm({ ...form, priceHN: e.target.value })} />
              <TextField label={pt ? 'Preço H.E.' : 'Price H.E.'}
                size="small" variant="outlined" type="number"
                value={form.priceHE}
                disabled={!form.hasOvertime}
                onChange={e => setForm({ ...form, priceHE: e.target.value })}
                helperText={form.hasOvertime ? '' : (pt ? 'Activa H.E. à esquerda' : 'Enable overtime')} />
              <FormControlLabel
                style={{ marginLeft: 0 }}
                control={<Switch size="small" checked={form.hasOvertime}
                  onChange={e => setForm({ ...form, hasOvertime: e.target.checked })} />}
                label={<Typography style={{ fontSize: '0.72rem' }}>{pt ? 'Tem H.E.' : 'Has overtime'}</Typography>} />
            </Box>
            <TextField label={pt ? 'Notas (opcional)' : 'Notes'}
              size="small" variant="outlined" multiline minRows={1}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })} />
            <Box className={classes.formActions}>
              <Button onClick={cancelEdit} disabled={saving}
                style={{ textTransform: 'none', fontSize: '0.78rem' }}>
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={saveForm} disabled={saving} className={classes.saveButton}
                startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 16 }} />}>
                {pt ? 'Guardar' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {loading && codes.length === 0 ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} style={{ color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.8rem' }}>{pt ? 'A carregar…' : 'Loading…'}</Typography>
          </Box>
        ) : grouped.length === 0 ? (
          <Box className={classes.empty}>
            <ReceiptIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {search || filterCategory
                ? (pt ? 'Sem resultados.' : 'No results.')
                : (pt ? 'Ainda não há códigos.' : 'No codes yet.')}
            </Typography>
            {!search && !filterCategory && (
              <Button className={classes.addButton} startIcon={<AddIcon style={{ fontSize: 18 }} />}
                onClick={startCreate}>
                {pt ? 'Criar primeiro' : 'Create first one'}
              </Button>
            )}
          </Box>
        ) : grouped.map(group => (
          <Box key={group.key} className={classes.categorySection}>
            <Box className={classes.categoryHeader} style={{ background: group.bg, color: group.color }}>
              {pt ? group.labelPt : group.labelEn} · {group.items.length}
            </Box>
            {group.items.map(c => (
              <Box key={c.id} className={classes.card}
                style={{ opacity: c.isActive ? 1 : 0.55 }}>
                <Box className={classes.codeBadge}>{c.code}</Box>
                <Box>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a' }}>
                    {c.description}
                  </Typography>
                  <Box style={{ display: 'flex', gap: 8, marginTop: 2, alignItems: 'center' }}>
                    {c.unit && (
                      <Chip label={`/ ${c.unit}`} size="small"
                        style={{ height: 16, fontSize: '0.55rem', background: '#f1f5f9', color: '#475569', fontWeight: 700 }} />
                    )}
                    {c.hasOvertime && (
                      <Chip label="H.E." size="small"
                        style={{ height: 16, fontSize: '0.55rem', background: '#fef3c7', color: '#92400e', fontWeight: 700 }} />
                    )}
                    {!c.isActive && (
                      <Chip label={pt ? 'Inactivo' : 'Inactive'} size="small"
                        style={{ height: 16, fontSize: '0.55rem', background: '#e2e8f0', color: '#475569' }} />
                    )}
                    {c.notes && (
                      <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        {c.notes}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box className={classes.priceBox}>
                  <Typography className={classes.priceVal}>
                    {c.priceHN != null ? c.priceHN.toLocaleString('pt-PT') : '—'}
                    {c.priceHN != null && <span style={{ fontSize: '0.62rem', color: '#94a3b8', marginLeft: 3, fontWeight: 600 }}>{currencySymbol}</span>}
                  </Typography>
                  <Typography className={classes.priceLabel}>H.N.</Typography>
                </Box>
                <Box className={classes.priceBox} style={{ opacity: c.hasOvertime ? 1 : 0.3 }}>
                  <Typography className={classes.priceVal}>
                    {c.priceHE != null ? c.priceHE.toLocaleString('pt-PT') : '—'}
                    {c.priceHE != null && <span style={{ fontSize: '0.62rem', color: '#94a3b8', marginLeft: 3, fontWeight: 600 }}>{currencySymbol}</span>}
                  </Typography>
                  <Typography className={classes.priceLabel}>H.E.</Typography>
                </Box>
                <Box className={classes.cardActions}>
                  <Tooltip title={c.isActive ? (pt ? 'Desactivar' : 'Deactivate') : (pt ? 'Reactivar' : 'Reactivate')}>
                    <IconButton size="small" onClick={() => toggleActive(c)}>
                      {c.isActive
                        ? <CloseIcon style={{ fontSize: 16, color: '#94a3b8' }} />
                        : <CheckIcon style={{ fontSize: 16, color: '#22c55e' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Editar' : 'Edit'}>
                    <IconButton size="small" onClick={() => startEdit(c)} disabled={editingId !== null}>
                      <EditIcon style={{ fontSize: 14, color: '#3b82f6' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Eliminar (soft)' : 'Delete (soft)'}>
                    <IconButton size="small" onClick={() => setDeleteConfirm({ id: c.id, code: c.code })}>
                      <DeleteIcon style={{ fontSize: 14, color: '#ef4444' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={pt ? 'Desactivar código?' : 'Deactivate code?'}
        message={deleteConfirm
          ? (pt ? `"${deleteConfirm.code}" passa a desactivado. Pode ser reactivado.` : `"${deleteConfirm.code}" will be deactivated.`)
          : ''}
        severity="warning"
        confirmLabel={pt ? 'Desactivar' : 'Deactivate'}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteNow}
        onClose={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default AccountingCodesView;
